import { TabContext } from '@mui/lab';
import { Box, Stack } from '@mui/material';
import SwipeableViews from 'react-swipeable-views-react-18-fix';
import useDisplayLanguageSection from '../display-information-field.hooks';
import {
  generateLanguageLabel,
  languageOptions,
} from '../display-information-field.utils';
import DisplayLanguageTabPanel from './display-language-tab-panel/display-language-tab-panel.component';
import { useEffect, useMemo, useState } from 'react';
import DisplayLanguageHandleDropdown from './display-language-handle-dropdown/display-language-handle-dropdown.component';
import DisplayLanguageDroppableWrapper from './display-language-droppable-wrapper/display-language-droppable-wrapper.component';
import { DisplayMultiLanguageSwitchProps } from '../display-information-field.component';
import { AnimatePresence } from 'framer-motion';
import { FormFieldPresence } from 'components/motion/motion.component';
import IncSwitchBase from '../../inc-switch-field/inc-switch-base/inc-switch-base.component';
import { useTranslation } from 'react-i18next';

type DisplayLanguageTabsProps = {
  displayFieldSet: (index: number) => React.ReactNode;
  displayMultiLanguageSwitchProps?: DisplayMultiLanguageSwitchProps;
};

const DisplayLanguageTabs = ({
  displayFieldSet,
  displayMultiLanguageSwitchProps = {},
}: DisplayLanguageTabsProps) => {
  const { t } = useTranslation();
  const {
    displayInformation,
    handleDisplayInformation,
    fieldState,
    activeTab,
    handleLanguageDropdown,
    handleTabChange,
    handleTabDragEnd,
    handleTabPanelChange,
  } = useDisplayLanguageSection();

  const options = useMemo(() => languageOptions(), []);
  const value = useMemo(
    () =>
      displayInformation?.map(({ languageCode }) =>
        options.find(opt => opt.id === languageCode),
      ),
    [displayInformation, options],
  );

  const [multiLanguageEnable, setMultiLanguageEnable] =
    useState<boolean>(false);

  // Handle multi-languages Switch
  const handleMultiLanguageEnable = () => {
    if (multiLanguageEnable) {
      handleDisplayInformation(displayInformation?.slice(0, 1));
    }
    setMultiLanguageEnable(!multiLanguageEnable);
  };

  useEffect(() => {
    setMultiLanguageEnable(displayInformation?.length > 1);
  }, [displayInformation?.length]);

  const { languageSwitchProps } = displayMultiLanguageSwitchProps;

  return (
    <Box>
      <Box>
        <IncSwitchBase
          value={multiLanguageEnable}
          onChange={handleMultiLanguageEnable}
          FormControlProps={{ sx: { mt: 0 } }}
          data-testid="MultiLanguageEnable"
          error={fieldState.error}
          label={
            displayMultiLanguageSwitchProps?.languageSwitchProps?.label ??
            `${t(
              'forms.fields.displayInformationField.multiSwitch.label',
            )}${generateLanguageLabel(displayInformation)}`
          }
          helperText={
            displayMultiLanguageSwitchProps?.languageSwitchProps?.helperText ??
            t('forms.fields.displayInformationField.multiSwitch.helperText')
          }
          {...languageSwitchProps}
        />
      </Box>
      <Box width="100%">
        <TabContext value={activeTab}>
          <Box>
            <AnimatePresence initial={false}>
              {multiLanguageEnable && (
                <FormFieldPresence>
                  <Box borderBottom={1} borderColor="divider">
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      display={{ sm: 'inline-flex' }}
                    >
                      <Box flex={1} minWidth={0}>
                        <DisplayLanguageDroppableWrapper
                          value={value}
                          handleTabChange={handleTabChange}
                          handleTabDragEnd={handleTabDragEnd}
                        />
                      </Box>
                      <Box flexShrink={0}>
                        <DisplayLanguageHandleDropdown
                          options={options}
                          value={value}
                          onChange={handleLanguageDropdown}
                        />
                      </Box>
                    </Stack>
                  </Box>
                </FormFieldPresence>
              )}
            </AnimatePresence>
            <SwipeableViews
              index={displayInformation
                ?.map(info => info.languageCode)
                .indexOf(activeTab)}
              data-testid="DisplayLanguageTabPanel.Section"
              onChangeIndex={handleTabPanelChange}
              enableMouseEvents
            >
              {value?.length > 0 &&
                value?.map((option, index) => (
                  <DisplayLanguageTabPanel
                    index={index}
                    key={option.id}
                    langCode={option.id}
                    displayFieldSet={displayFieldSet}
                    data-testid={`DisplayLanguageTabPanel.${option.id}`}
                    multiLanguageEnable={multiLanguageEnable}
                  />
                ))}
            </SwipeableViews>
          </Box>
        </TabContext>
      </Box>
    </Box>
  );
};

export default DisplayLanguageTabs;
